import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { InstagramFeedPost, InstagramFeedPostSortField } from '@anytag/graphql';

interface Props {
  isComparePage?: boolean;
}

export const useAnalyticsInstagramPostsListTable = ({ isComparePage }: Props) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<InstagramFeedPost>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => (
        <TableCell.Post
          title={row.original.content || ''}
          thumbnail={row.original.thumbnail || ''}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.ViewHelp'),
        sortField: InstagramFeedPostSortField.VIEW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'viewsByFollowersRate',
      header: t<string>('HeaderColumn.Views by Followers'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsByFollowersHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_BY_FOLLOWERS_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.viewsByFollowersRate, false)} unit="%" />
    },
    {
      accessorKey: 'viewsByNonFollowersRate',
      header: t<string>('HeaderColumn.Views by Non-Followers'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsByNonFollowersHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_BY_NON_FOLLOWERS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.viewsByNonFollowersRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'viewsFromHome',
      header: t<string>('HeaderColumn.Views from Home'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsFromHomeHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_FROM_HOME
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.viewsFromHome)} />
    },
    {
      accessorKey: 'viewsFromProfile',
      header: t<string>('HeaderColumn.Views from Profile'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsFromProfileHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_FROM_PROFILE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.viewsFromProfile)} />
    },
    {
      accessorKey: 'viewsFromDiscovery',
      header: t<string>('HeaderColumn.Views from Discovery'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsFromDiscoveryHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_FROM_DISCOVERY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.viewsFromDiscovery)} />
    },
    {
      accessorKey: 'viewsFromOthers',
      header: t<string>('HeaderColumn.Views from Others'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.viewsFromOthersHelp'),
        sortField: InstagramFeedPostSortField.VIEWS_FROM_OTHERS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.viewsFromOthers)} />
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.ReachPostHelp'),
        sortField: InstagramFeedPostSortField.REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reach)} />
    },
    {
      accessorKey: 'nonFollowerReach',
      header: t<string>('HeaderColumn.Non Follower Reach'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.nonFollowerReachHelp'),
        sortField: InstagramFeedPostSortField.NON_FOLLOWER_REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.nonFollowerReach || 0)} />
    },
    {
      accessorKey: 'interaction',
      header: t<string>('HeaderColumn.Interaction'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.interactionHelp'),
        sortField: InstagramFeedPostSortField.INTERACTION
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.interaction || 0)} />
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.EngagementHelp'),
        sortField: InstagramFeedPostSortField.ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.engagement)} />
    },
    {
      accessorKey: 'engagementRateByFollowers',
      header: t<string>('HeaderColumn.Engagement Rate by Followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementRateByFollowersHelp'),
        sortField: InstagramFeedPostSortField.ENGAGEMENT_RATE_BY_FOLLOWERS
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.engagementRateByFollowers, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagementRateByViews',
      header: t<string>('HeaderColumn.Engagement Rate by Views'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.EngagementRateHelp'),
        sortField: InstagramFeedPostSortField.ENGAGEMENT_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.engagementRateByViews, false)} unit="%" />
    },
    {
      accessorKey: 'like',
      header: t<string>('HeaderColumn.Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeHelp'),
        sortField: InstagramFeedPostSortField.LIKE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comment',
      header: t<string>('HeaderColumn.Comment'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.CommentHelp'),
        sortField: InstagramFeedPostSortField.COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'share',
      header: t<string>('HeaderColumn.share'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.shareHelp'),
        sortField: InstagramFeedPostSortField.SHARE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.share || 0)} />
    },
    {
      accessorKey: 'save',
      header: t<string>('HeaderColumn.Save'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.SaveHelp'),
        sortField: InstagramFeedPostSortField.SAVED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saved)} />
    },
    {
      accessorKey: 'accountsEngaged',
      header: t<string>('HeaderColumn.Accounts Engaged'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.accountsEngagedHelp'),
        sortField: InstagramFeedPostSortField.ACCOUNTS_ENGAGED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.accountsEngaged || 0)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>('HeaderColumn.Profile Activity'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.profileActivityHelp'),
        sortField: InstagramFeedPostSortField.PROFILE_ACTIVITY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.profileActivity || 0)} />
    },
    {
      accessorKey: 'profileVisits',
      header: t<string>('HeaderColumn.Profile Visits'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.profileVisitsHelp'),
        sortField: InstagramFeedPostSortField.PROFILE_VISITS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.profileVisits || 0)} />
    },
    {
      accessorKey: 'externalLinkTaps',
      header: t<string>('HeaderColumn.External Link Taps'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.externalLinkTapsHelp'),
        sortField: InstagramFeedPostSortField.EXTERNAL_LINK_TAPS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.externalLinkTaps || 0)} />
    },
    {
      accessorKey: 'follow',
      header: t<string>('HeaderColumn.Follow'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.FollowHelp'),
        sortField: InstagramFeedPostSortField.FOLLOW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.follow || 0)} />
    },
    {
      accessorKey: 'productPageView',
      header: t<string>('HeaderColumn.Product Page View'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.productPageViewHelp'),
        sortField: InstagramFeedPostSortField.PRODUCT_PAGE_VIEW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.productPageView || 0)} />
    },
    {
      accessorKey: 'productButtonClick',
      header: t<string>('HeaderColumn.Product Button Click'),
      meta: {
        align: 'right',
        hidden: isComparePage,
        help: t<string>('Tooltip.productButtonClickHelp'),
        sortField: InstagramFeedPostSortField.PRODUCT_BUTTON_CLICK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.productButtonClick || 0)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { help: t<string>('Tooltip.PostDateHelp'), sortField: InstagramFeedPostSortField.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postDate} />
    }
  ];

  return { columns };
};
